import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE } from "../../apiBase";
import ModalForHide from "../Products/Components/ModalForHide";
import NotDirectLinkPopup, { warningSuppliers } from "../Products/Components/NotDirectLinkPopup";
import { signupDiscountSuppliers } from "../Products/Components/RegisterForDiscount";
import { OOSbyZIPSuppliers } from "../Products/Components/OOSbyZIP";
import MessageBeforeLinkPopup from "../Products/Components/MessageBeforeLinkPopup";
import FavoritesIcon from "../Products/Components/FavoritesIcon";
import ModalForVariationsTable from "../Products/Components/ModalForVariationsTable";
import AdvancedData from "../Products/Components/AdvancedData";
import logo from "../../logo2.svg";
import { useAuth0 } from "@auth0/auth0-react";
import { checkGatedBatch, gatedCheckProgress, gatedFiltersActive, gatedVisibleProducts, nextUncheckedAsins, productProfit, productRoi } from "../../utils";
import { getAmazonConsentUrl } from "../../utils_amazon";

const PAGE_SIZE = 10;
// One look-ahead round. Amazon's restrictions endpoint is the real ceiling and checkGatedBatch
// already splits a round into parallel requests of 15, so a bigger round would not finish any
// sooner -- it would only pile up work server-side and update the progress text less often.
const LOOKAHEAD_ROUND = 50;
// How many ASINs one page may spend looking for enough ungated rows before it stops and asks.
// A sparse result set can run to thousands of gated ASINs, and sweeping all of them is not
// something the user asked for by ticking a filter.
const LOOKAHEAD_BUDGET = 300;

export default function ProductsTable(props) {
  const [hideModalProduct, setHideModalProduct] = useState("");
  const { isAuthenticated, isLoading, getAccessTokenSilently, user } = useAuth0();
  const [linkUrl, setLinkUrl] = useState("");
  const [prep, setPrep] = useState(localStorage.getItem("costPrep") || 0);
  const [advancedDataProduct, setAdvancedDataProduct] = useState(null);
  const [showMessageObj, setShowMessageObj] = useState(null);
  const [ctrlClickTip, setCtrlClickTip] = useState("");
  const [searchModal, setSearchModal] = useState(null);
  const [saveMoney, setSaveMoney] = useState(null);
  const [saveMoneyRates, setSaveMoneyRates] = useState({});
  const [modalForVariations, setModalForVariations] = useState(false);
  const [modalForVariationsAsin, setModalForVariationsAsin] = useState("");
  const [loginWAZ, setLoginWAZ] = useState(false);
  const [headerStyle, setHeaderStyle] = useState({});
  const [loadGated, setLatedGated] = useState(false);
  // Set when a page has spent its look-ahead budget without filling. It is state rather than a
  // ref because it paints the checkpoint row, and clearing it is what resumes the sweep.
  const [fillExhausted, setFillExhausted] = useState(false);
  // ASINs already on the wire. Results merge one 15-ASIN batch at a time, which re-runs the
  // look-ahead mid-sweep; without this it would ask for the same ASINs again.
  const inFlightRef = useRef(new Set());
  // Remaining ASINs this page may check. A ref, not state: as state every decrement would
  // re-run the effect on its own and feed itself.
  const budgetRef = useRef(LOOKAHEAD_BUDGET);
  const gatedTimeoutRef = useRef(null);
  // ProductFinder (Master Search) renders this table without the scan-progress prop. Calling it
  // unguarded threw before setLatedGated(false) could run, leaving the flag stuck on.
  const setIsProductsLoading = props.setIsProductsLoading || (() => { });
  const mainProductPage = ["big5sportinggoods", "dillards", "famousfootwear", "kohls", "dickssportinggoods", "boscovs", "jcpenney", "fivebelow", "nike", "belk", "underarmour", "crocs", "champssports", "sephora"]
  const categorySalesRankCount = { 'Climate Pledge Friendly': 1500000, 'Kindle Store': 6756566, 'Video Shorts': 127580, 'Apps & Games': 936100, 'Baby Products': 4172515, 'Digital Music': 68941656, 'Alexa Skills': 112529, 'Toys & Games': 9204843, 'Patio, Lawn & Garden': 22608266, 'Books': 102997339, 'Arts, Crafts & Sewing': 16296162, 'Software': 177161, 'Sports & Outdoors': 41416624, 'Gift Cards': 28409, 'Video Games': 1204647, 'Handmade Products': 3644844, 'Clothing, Shoes & Jewelry': 275101033, 'Office Products': 12266328, 'Grocery & Gourmet Food': 3869382, 'Tools & Home Improvement': 37997159, 'Movies & TV': 7740340, 'Musical Instruments': 2893522, 'Collectibles & Fine Art': 8802597, 'Appliances': 1565048, 'Pet Supplies': 8652837, 'Cell Phones & Accessories': 26169713, 'Baby': 4172515, 'Industrial & Scientific': 20388882, 'Everything Else': 8317014, 'CDs & Vinyl': 8366789, 'Beauty & Personal Care': 14890600, 'Home & Kitchen': 161989558, 'Electronics': 25856893, 'Automotive': 46255283, 'Magazine Subscriptions': 42578, 'Health & Household': 11049107, 'Audible Books & Originals': 743380, 'Kitchen & Dining': 37818304, "Amazon Devices": 1894, "Computers & Accessories": 14064999, "Camera & Photo Products": 4021127, "Collectible Coins": 210648, "Camera & Photo": 4021127 }

  // Sorted once, on a copy: the old code called .sort() straight on props.data, mutating the
  // array the parent owns.
  const sorted = useMemo(() => {
    const isNew = (p) => (props.newAsins && props.newAsins.includes(p.asin.split("/").pop().replace("B0", "")) ? -1 : 1);
    return [...(props.data ?? [])].sort((a, b) => isNew(a) - isNew(b));
  }, [props.data, props.newAsins]);

  const gatedActive = gatedFiltersActive(props.filters);
  // Gated rows are dropped BEFORE the page slice, so ten slots hold ten matching products
  // instead of however many of a fixed window survived.
  const visible = useMemo(
    () => gatedVisibleProducts(sorted, props.filters ?? {}, props.gatedStatus),
    [sorted, props.filters, props.gatedStatus]
  );
  const pageRows = useMemo(
    () => visible.slice((props.page - 1) * PAGE_SIZE, props.page * PAGE_SIZE),
    [visible, props.page]
  );
  const gatedProgress = useMemo(
    () => gatedCheckProgress(sorted, props.gatedStatus),
    [sorted, props.gatedStatus]
  );
  const allAsinsChecked = gatedProgress.checked >= gatedProgress.total;
  const hasSeller = Boolean(props.sellerId && props.sellerId !== "none" && props.sellerId !== "");
  const hasNextPage = visible.length > props.page * PAGE_SIZE;
  const stillFilling = gatedActive && loadGated;

  const keepLooking = () => {
    budgetRef.current = LOOKAHEAD_BUDGET;
    setFillExhausted(false);
  };

  const handleScroll = () => {
    try {
      const table = document.getElementById('tableTable');
      const tableRect = table.getBoundingClientRect();

      if (tableRect.top <= 0) {
        // The header is out of view (or at the top of the viewport)
        setHeaderStyle({
          position: 'relative',
          top: -tableRect.top + "px",
          zIndex: 9,
        });
      } else {
        setHeaderStyle({});
      }
    } catch { }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // A fresh page, or a change to either gated toggle, gets a fresh look-ahead budget.
  useEffect(() => {
    budgetRef.current = LOOKAHEAD_BUDGET;
    setFillExhausted(false);
  }, [props.page, props.filters?.filterOutGated, props.filters?.showOnlyUngated]);

  useEffect(() => {
    if (!isLoading && isAuthenticated && props.data && props.data.length > 0) {
      setAdvancedDataProduct(null);
      if (localStorage.getItem("azlog_pop_first") !== "1") {
        setLoginWAZ(true);
        localStorage.setItem("azlog_pop_first", "1");
      }
    }
  }, [isLoading, isAuthenticated, props.data, props.page]);

  useEffect(() => {
    const clearGatedTimeout = () => {
      if (gatedTimeoutRef.current) {
        clearTimeout(gatedTimeoutRef.current);
        gatedTimeoutRef.current = null;
      }
    };

    const checkGated = async (asins) => {
      if (!props.sellerId) return;
      asins.forEach((asin) => inFlightRef.current.add(asin));
      setLatedGated(true);
      // checkGatedBatch has no request timeout of its own. Without this cap a call that never
      // settles would leave the table reporting "checking" and Next Page disabled forever.
      clearGatedTimeout();
      gatedTimeoutRef.current = setTimeout(() => {
        asins.forEach((asin) => inFlightRef.current.delete(asin));
        setLatedGated(false);
        setFillExhausted(true);
      }, 40000);
      try {
        await checkGatedBatch(props.sellerId, asins, props.refreshToken, (data) => {
          Object.entries(data).forEach(([asin, response]) => {
            props.setGatedStatus((prevGS) => ({
              ...prevGS,
              [asin]: response,
            }));
          });
        });
      } finally {
        clearGatedTimeout();
        asins.forEach((asin) => inFlightRef.current.delete(asin));
        setIsProductsLoading(false);
        setLatedGated(false);
      }
    };

    if (!isLoading && isAuthenticated && sorted.length > 0 && hasSeller) {
      let asinsToCheck = [];

      if (!gatedActive) {
        // No gated filter: eligibility is only decoration on the rows already on screen, so
        // ask about those ten and nothing more.
        asinsToCheck = nextUncheckedAsins(pageRows, props.gatedStatus, inFlightRef.current, PAGE_SIZE);
      } else if (!fillExhausted && inFlightRef.current.size === 0) {
        // Fill toward one row past the page, so "is there a next page?" has an answer without
        // paying for a whole extra page of checks.
        const target = props.page * PAGE_SIZE + 1;
        if (visible.length < target) {
          const room = Math.min(LOOKAHEAD_ROUND, budgetRef.current);
          if (room > 0) {
            asinsToCheck = nextUncheckedAsins(sorted, props.gatedStatus, inFlightRef.current, room);
            budgetRef.current -= asinsToCheck.length;
          } else {
            // Budget spent and the page is still short -- stop and let the user decide.
            setFillExhausted(true);
          }
        }
      }

      if (asinsToCheck.length > 0) {
        checkGated(asinsToCheck);
      } else if (inFlightRef.current.size === 0) {
        setLatedGated(false);
      }
    }

    // No cleanup that clears the timeout here: this effect re-runs on every arriving batch, and
    // tearing the cap down each time would leave a genuinely hung request uncapped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, sorted, pageRows, visible, gatedActive, fillExhausted, props.page, props.gatedStatus, props.sellerId, hasSeller]);

  useEffect(() => () => {
    if (gatedTimeoutRef.current) clearTimeout(gatedTimeoutRef.current);
  }, []);

  useEffect(() => {
    const getSaveMoneyRates = async () => {
      setSaveMoneyRates({})
      const accessToken = await getAccessTokenSilently();
      fetch(
        API_BASE + "/get_cashback_rates/" + saveMoney + "/", { mode: "cors", headers: { Authorization: `Bearer ${accessToken}` } }
      )
        .then((response) => response.json())
        .then((data) => {
          if (saveMoney !== null) {
            setSaveMoneyRates(data);
          }
        });
    };

    if (!isLoading && isAuthenticated && saveMoney !== null) {
      getSaveMoneyRates();
    }
  }, [getAccessTokenSilently, isLoading, isAuthenticated, saveMoney]);

  const openSuppLink = (product) => {
    if (product.source_name === "b2bsportswear") {
      product.url = product.url.replace("+", "").replace("--", "-")
    }
    if (!warningSuppliers.includes(product.source_name)) {
      window.open(
        product.url.replace(
          "X".repeat(product.source_name.length),
          product.source_name
        )
      );
    } else {
      setLinkUrl(
        product.url.replace(
          "X".repeat(product.source_name.length),
          product.source_name
        )
      );
    }
  };

  const getKeepaImage = async (image_asin) => {
    const accessToken = await getAccessTokenSilently();
    const response = await fetch(
      API_BASE + "/get_keepa_image/" + image_asin + "/",
      { mode: "cors", headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const data = await response.json();
    return data.url;
  };

  const reportMismatch = async (asin, source) => {
    const accessToken = await getAccessTokenSilently();
    const now = Date.now();
    const lastReport = localStorage.getItem("lastReport");
    if (lastReport && now - Number(lastReport) < 10000) {
      alert(`Please wait ${10 - Math.floor((now - Number(lastReport)) / 1000)} seconds before reporting for more mismatches!`);
      return;
    }

    const lastReportDay = localStorage.getItem("lastReportDay");
    const lastReportAmount = Number(localStorage.getItem("lastReportAmount")) || 0;
    const today = new Date().toISOString().slice(0, 10);
    let reportAmount = lastReportAmount;
    if (lastReportDay === today) {
      if (reportAmount >= 30) {
        alert("You have reached the maximum of 30 reports for today.");
        return;
      } else {
        reportAmount += 1;
      }
    } else {
      reportAmount = 1;
      localStorage.setItem("lastReportDay", today);
    }
    localStorage.setItem("lastReportAmount", reportAmount);
    localStorage.setItem("lastReport", now);
    fetch(API_BASE + "/report_mismatch/" + asin + "/" + source + "/", {
      mode: "cors",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  };

  return (
    <>
      <div className="table-container products-table-container">
        <table id="tableTable" className="table is-hoverable products-table">
          <thead style={headerStyle}>
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th>
                <abbr
                  title="BSR"
                  style={{
                    display:
                      props.filters.hasOwnProperty("sortBy") &&
                      props.filters["sortBy"][0] === "sales_rank" &&
                      "flex",
                    textDecoration: props.setSortASC === undefined && "none"
                  }}
                  onClick={() => {
                    props.setSortASC(!props.sortASC);
                    props.setFilters((filters) => ({
                      ...filters,
                      sortBy: ["sales_rank", props.sortASC],
                    }));
                  }}
                >
                  BSR
                  {props.filters.hasOwnProperty("sortBy") &&
                    props.filters["sortBy"][0] === "sales_rank" && (
                      <i
                        className={
                          "fa fa-arrow-" +
                          (props.filters["sortBy"][1] ? "up" : "down")
                        }
                        aria-hidden="true"
                        style={{ marginTop: "5px" }}
                      ></i>
                    )}
                </abbr>
              </th>
              <th>ASIN</th>
              <th>
                <abbr
                  title="Price"
                  style={{
                    display:
                      props.filters.hasOwnProperty("sortBy") &&
                      props.filters["sortBy"][0] === "price" &&
                      "flex",
                    textDecoration: props.setSortASC === undefined && "none"
                  }}
                  onClick={() => {
                    props.setSortASC(!props.sortASC);
                    props.setFilters((filters) => ({
                      ...filters,
                      sortBy: ["price", props.sortASC],
                    }));
                  }}
                >
                  Price
                  {props.filters.hasOwnProperty("sortBy") &&
                    props.filters["sortBy"][0] === "price" && (
                      <i
                        className={
                          "fa fa-arrow-" +
                          (props.filters["sortBy"][1] ? "up" : "down")
                        }
                        aria-hidden="true"
                        style={{ marginTop: "5px" }}
                      ></i>
                    )}
                </abbr>
              </th>
              <th class="tooltip">AZPrice<span style={{ fontSize: "65%" }}>(?)</span>
                <span class="tooltiptext" style={{ marginTop: "-15%" }}>
                  Lowest New Price *Include Shipping
                  <img style={{ all: "unset", width: "8.5vw" }} loading="lazy" alt="azprice" src="/assets/azprice.png" />
                </span></th>
              <th class="tooltip">
                AZFees<span style={{ fontSize: "62%" }}>(?)</span>
                <span class="tooltiptext">
                  Amazon Referral Fee {!(props.filters.hasOwnProperty("isFBM") && props.filters.isFBM) && "+ Fulfillment Cost (FBA)"}
                </span>
              </th>
              <th class="tooltip" style={{ fontSize: "95%" }}>
                Costs<span style={{ fontSize: "62%" }}>(?)</span>
                <span class="tooltiptext">
                  Input any additional costs, such as prep cost, FBM, and
                  others.
                </span>
              </th>
              <th>AZOffers</th>
              <th>
                <abbr
                  title="Profit"
                  style={{
                    display:
                      props.filters.hasOwnProperty("sortBy") &&
                      props.filters["sortBy"][0] === "profit" &&
                      "flex",
                    textDecoration: props.setSortASC === undefined && "none"
                  }}
                  onClick={() => {
                    props.setSortASC(!props.sortASC);
                    props.setFilters((filters) => ({
                      ...filters,
                      sortBy: ["profit", props.sortASC],
                    }));
                  }}
                >
                  Profit
                  {props.filters.hasOwnProperty("sortBy") &&
                    props.filters["sortBy"][0] === "profit" && (
                      <i
                        className={
                          "fa fa-arrow-" +
                          (props.filters["sortBy"][1] ? "up" : "down")
                        }
                        aria-hidden="true"
                        style={{ marginTop: "5px" }}
                      ></i>
                    )}
                </abbr>
              </th>
              <th>
                <abbr
                  title="ROI"
                  style={{
                    display:
                      props.filters.hasOwnProperty("sortBy") &&
                      props.filters["sortBy"][0] === "roi" &&
                      "flex",
                    textDecoration: props.setSortASC === undefined && "none"
                  }}
                  onClick={() => {
                    props.setSortASC(!props.sortASC);
                    props.setFilters((filters) => ({
                      ...filters,
                      sortBy: ["roi", props.sortASC],
                    }));
                  }}
                >
                  ROI
                  {props.filters.hasOwnProperty("sortBy") &&
                    props.filters["sortBy"][0] === "roi" && (
                      <i
                        className={
                          "fa fa-arrow-" +
                          (props.filters["sortBy"][1] ? "up" : "down")
                        }
                        aria-hidden="true"
                        style={{ marginTop: "5px" }}
                      ></i>
                    )}
                </abbr>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(props.data.length === 0 || pageRows.length === 0) && props.isProductsLoading === false && !stillFilling && <td style={{ textAlign: "center" }} colSpan={12}>No products.{props.data.length === 0 && " No scan credit used."} Have you tried our Live Search yet?</td>}
            {pageRows.map((product, index) => {
              return (<>
                  <tr key={index}>
                    <td style={{ width: "22vw" }}>
                      <div style={{ display: "flex" }}>
                        <div className="amazonImage">
                          <p
                            onClick={() => {
                              window.open(product.asin + "?th=1&psc=1");
                            }}
                            style={{ textAlign: "center", cursor: "pointer" }}
                          >
                            Amazon
                            {props.newAsins && props.newAsins.includes(product.asin.split("/")[product.asin.split("/").length - 1].replace("B0", "")) && <span style={{ backgroundColor: "lightgreen", padding: "0% 5%", borderRadius: "30px", marginLeft: "5%" }}>NEW</span>}
                          </p>
                          <img
                            alt="product_image"
                            className="hoverImageTable"
                            loading="lazy"
                            src={(product.az_img !== null && product.az_img !== "") ?
                              product.az_img :
                              `https://images.amazon.com/images/P/${product.asin.split("/")[product.asin.split("/").length - 1]}.jpg`}
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null; // prevents looping
                              currentTarget.src = "/assets/noimage.png";
                            }}
                            style={{
                              borderRadius: "4.5vw",
                              border: "5px solid #E3D0DF",
                              height: "4.5vw",
                              width: "4.5vw",
                              objectFit: "scale-down",
                              position: "relative",
                              zIndex: "5"
                            }}
                            onMouseEnter={() => {
                              if (localStorage.getItem("CtrlClickTipCount") === null || localStorage.getItem("CtrlClickTipCount") < 15) {
                                setCtrlClickTip(product.asin);
                                localStorage.setItem("CtrlClickTipCount", localStorage.getItem("CtrlClickTipCount") === null ? 1 : parseInt(localStorage.getItem("CtrlClickTipCount")) + 1);
                              }
                            }}
                            onMouseLeave={() => setCtrlClickTip("")}
                            onClick={() => {
                              window.open(product.asin + "?th=1&psc=1");
                            }}
                          />
                          <i class="fa fa-external-link" title="Open the two links at the same time" aria-hidden="true" style={{ position: "absolute", left: "15.8vw", marginTop: "-1vw", cursor: "pointer" }} onClick={() => {
                            let message = "";
                            if (mainProductPage.includes(product.source_name)) {
                              message +=
                                "You will see the main product page.\nYou will need to set the correct variation yourself.\n'..." + product.title.split(" | At")[0].slice((product.title.split(" | At")[0].length / 2).toFixed(0)) + "'<br/>";
                            }
                            if (
                              signupDiscountSuppliers.includes(
                                product.source_name
                              )
                            ) {
                              message +=
                                "To receive the lowest price, this supplier requires you to sign up to their website.";
                            }
                            if (
                              OOSbyZIPSuppliers.includes(
                                product.source_name
                              )
                            ) {
                              message +=
                                "\nIf the product is out of stock - Change the ZIP CODE!\n";
                            }
                            if (message !== "" && (localStorage.getItem("showSuppMesssage" + product.source_name) === 'true' || localStorage.getItem("showSuppMesssage" + product.source_name) === null)) {
                              setShowMessageObj({
                                message: message,
                                messageAction: openSuppLink,
                                messageActionArg: product,
                                source: product.source_name
                              });
                            } else {
                              openSuppLink(product);
                            }
                            let secondOpened = window.open(product.asin + "?th=1&psc=1");
                            if (!secondOpened) {
                              alert("Please enable pop-ups in your browser to continue.")
                            }
                          }}></i>
                        </div>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "10vw",
                            height: "10vh",
                            padding: "9px",
                            textAlign: "center",
                            margin: "auto",
                            fontSize: "77.5%"
                          }}
                        >
                          {product.title.split(" | At")[0].slice(0, 40)}...{product.title.split(" | At")[0].slice(-25)}
                          <span className="tooltip" style={{ position: "absolute", marginLeft: "2px" }}>
                            <i class="fa fa-info-circle" id="variationTt" aria-hidden="true" style={{ fontSize: "90%" }}></i>
                            <span className="tooltiptext" style={{ zIndex: "999", padding: "5px" }}>
                              {product.title.split(" | At")[0]}
                            </span>
                          </span>
                        </span>
                        {ctrlClickTip === product.asin && <span style={{ position: "absolute", backgroundColor: "#E3D0DF", zIndex: "999", marginLeft: "6.5vw", padding: "5px", fontSize: "70%", marginTop: "10vh" }}>
                          Tip: Hold CTRL + Click.
                        </span>}
                        <div className="sourceImage">
                          <p
                            onClick={() => {
                              let message = "";
                              if (mainProductPage.includes(product.source_name)) {
                                message +=
                                  "You will see the main product page.\nYou will need to set the correct variation yourself.\n'..." + product.title.split(" | At")[0].slice((product.title.split(" | At")[0].length / 2).toFixed(0)) + "'<br/>";
                              }
                              if (
                                signupDiscountSuppliers.includes(
                                  product.source_name
                                )
                              ) {
                                message +=
                                  "To receive the lowest price, this supplier requires you to sign up to their website.";
                              }
                              if (
                                OOSbyZIPSuppliers.includes(
                                  product.source_name
                                )
                              ) {
                                message +=
                                  "\nIf the product is out of stock - Change the ZIP CODE!\n";
                              }
                              if (message !== "" && (localStorage.getItem("showSuppMesssage" + product.source_name) === 'true' || localStorage.getItem("showSuppMesssage" + product.source_name) === null)) {
                                setShowMessageObj({
                                  message: message,
                                  messageAction: openSuppLink,
                                  messageActionArg: product,
                                  source: product.source_name
                                });
                              } else {
                                openSuppLink(product);
                              }
                            }}
                            style={{
                              textAlign: "center",
                              maxWidth: "4.5vw",
                              cursor: "pointer",
                            }}
                          >
                            {product.source_name.replace("-", "").replace("fragrancenet", "fragrancenetWS")}
                          </p>
                          <img
                            alt="product_image"
                            className="hoverImageTable"
                            loading="lazy"
                            src={product.img}
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null; // prevents looping
                              currentTarget.src = "/assets/noimage.png";
                            }}
                            style={{
                              borderRadius: "4.5vw",
                              border: "5px solid #E3D0DF",
                              height: "4.5vw",
                              width: "4.5vw",
                              objectFit: "scale-down",
                              position: "relative",
                              zIndex: "5"
                            }}
                            onMouseEnter={() => {
                              if (localStorage.getItem("CtrlClickTipCount") === null || localStorage.getItem("CtrlClickTipCount") < 15) {
                                setCtrlClickTip(product.asin);
                                localStorage.setItem("CtrlClickTipCount", localStorage.getItem("CtrlClickTipCount") === null ? 1 : parseInt(localStorage.getItem("CtrlClickTipCount")) + 1);
                              }
                            }}
                            onMouseLeave={() => setCtrlClickTip("")}
                            onClick={() => {
                              let message = "";
                              if (mainProductPage.includes(product.source_name)) {
                                message +=
                                  "You will see the main product page.\nYou will need to set the correct variation yourself.\n'..." + product.title.split(" | At")[0].slice((product.title.split(" | At")[0].length / 2).toFixed(0)) + "'<br/>";
                              }
                              if (
                                signupDiscountSuppliers.includes(
                                  product.source_name
                                )
                              ) {
                                message +=
                                  "To receive the lowest price, this supplier requires you to sign up to their website.";
                              }
                              if (
                                OOSbyZIPSuppliers.includes(
                                  product.source_name
                                )
                              ) {
                                message +=
                                  "\nIf the product is out of stock - Change the ZIP CODE!\n";
                              }
                              if (message !== "" && (localStorage.getItem("showSuppMesssage" + product.source_name) === 'true' || localStorage.getItem("showSuppMesssage" + product.source_name) === null)) {
                                setShowMessageObj({
                                  message: message,
                                  messageAction: openSuppLink,
                                  messageActionArg: product,
                                  source: product.source_name
                                });
                              } else {
                                openSuppLink(product);
                              }
                            }}
                          />
                          {(product.source_name === "boscovs" || product.source_name === "dickssportinggoods" || product.source_name === "b2bsportswear" || product.source_name === "joann" || product.source_name === "rei") &&
                            <div className="warningSign" style={{ borderBottom: "2px solid rgba(0,155,0,0.5)" }}>
                              <i class="fa fa-info-circle" aria-hidden="true"></i>
                              <span className="warText"> Select the correct variation on {product.source_name}'s website.</span>
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td id="categoryTt" style={{ color: !categorySalesRankCount[product.category] && "darkred" }}>{product.category}</td>
                    <td id="bsrTt" style={{ minWidth: "5.6vw", color: !categorySalesRankCount[product.category] && "darkred" }}>
                      #{product.sales_rank}{categorySalesRankCount[product.category] && <><br /><span style={{ fontSize: "83%" }}>Top {((product.sales_rank / categorySalesRankCount[product.category]) * 100) < 0.01 ? "<0.01" : ((product.sales_rank / categorySalesRankCount[product.category]) * 100).toFixed(((product.sales_rank / categorySalesRankCount[product.category]) * 100) < 0.1 ? 2 : 1)}%</span></>}
                      <br />
                      <img
                        loading="lazy"
                        src={`https://corsproxy.io/?https://graph.keepa.com/?asin=${product.asin.split("/")[
                          product.asin.split("/").length - 1
                        ]}&domain=com&salesrank=1&amazon=1&new=1&bb=1&fba=1&range=90`}
                        onMouseEnter={async (e) => {
                          if (e.target.alt !== "keepahistloaded") {
                            e.target.src = "data:image/svg+xml;base64," + btoa('<svg width="100" height="40" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#f5f5f5"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="12" fill="#666">Loading...</text></svg>');
                            const new_url = await getKeepaImage(product.asin.split("/")[product.asin.split("/").length - 1])
                            e.target.src = new_url
                            e.target.alt = "keepahistloaded"
                          }
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            'data:image/svg+xml;base64,' +
                            btoa('<svg width="100" height="40" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" fill="#f5f5f5"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="12" fill="#666">Hover to reveal...</text></svg>');
                        }}

                        alt="keepahist"
                        style={{ position: "relative", border: "1px solid gray" }}
                        className="keepagraphprd"
                        onClick={() =>
                          window.open(
                            `https://keepa.com/#!product/1-${product.asin.split("/")[
                            product.asin.split("/").length - 1
                            ]}`,
                            "_blank"
                          )
                        }
                      />
                    </td>
                    <td id="asinTt">
                      {props.newAsins && props.newAsins.includes(product.asin.split("/")[product.asin.split("/").length - 1].replace("B0", "")) && <span style={{ backgroundColor: "lightgreen", padding: "0% 5%", borderRadius: "30px" }}>NEW</span>}
                      <br />
                      <a
                        href={product.asin}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: "underline", color: "#8B3C7E" }}
                      >
                        {
                          product.asin.split("/")[
                          product.asin.split("/").length - 1
                          ]
                        }
                      </a>
                      <button
                        className="graphicon"
                        id="variationBtnTt"
                        style={{
                          width: "calc(1.2vh + 4.55vw)",
                          height: "calc(1.2vh + .75vw)",
                          borderRadius: "20px",
                          border: "2px solid gray",
                          objectFit: "contain",
                          fontSize: ".65vw",
                          cursor: "pointer",
                          color: "rgb(139, 60, 126)",
                          fontWeight: "600",
                          marginBottom: "7px",
                          backgroundColor: "white",
                        }}
                        onClick={() => {
                          setModalForVariationsAsin(product.asin.split("/")[product.asin.split("/").length - 1]);
                          setModalForVariations(true)
                        }}
                      >VARIATIONS</button>
                    </td>
                    <td id="buyPriceTt" style={{ width: "4.5vw", maxWidth: "4.5vw", paddingTop: "2%" }} class="bg">
                      ${(product.price * (1 - (props.filters.hasOwnProperty("priceCoupon") && (props.filters["priceCoupon"] / 100)))).toFixed(2)}
                      <button
                        className="graphicon"
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "20px",
                          border: "2px solid gray",
                          objectFit: "contain",
                          fontSize: "65%",
                          cursor: "pointer",
                          color: "rgb(139, 60, 126)",
                          fontWeight: "600",
                          marginBottom: "7px",
                          backgroundColor: "white",
                        }}
                        onClick={() => {
                          setSaveMoney(product.source_name)
                        }}
                      >Save $</button>
                    </td>
                    <td id="sellPriceTt" style={{ width: "6.5vw", maxWidth: "6.5vw" }} class="bg">
                      {product.az_price > 0 ? `$${product.az_price.toFixed(2)}` : "OOS on AMZ"}
                    </td>
                    <td id="azFeesTt">
                      {product.az_price > 0 ? (product.selling_on_az_fees === 0 || product.fba_cost === 0
                        ? "N/A"
                        : "-$" +
                        (product.selling_on_az_fees + (!props.filters.isFBM && product.fba_cost)).toFixed(
                          2
                        )) : "OOS on AMZ"}
                    </td>
                    <td id="costTt">
                      <input
                        type="number"
                        style={{ width: "3vw" }}
                        value={prep}
                        min={0}
                        step={0.25}
                        onChange={(e) => { setPrep(e.target.value); localStorage.setItem("costPrep", e.target.value) }}
                        onBlur={(e) => e.target.value === "" && setPrep(0)}
                      />
                    </td>
                    <td id="azOffersTt">
                      {JSON.parse(product.az_offers).length}
                      {JSON.parse(product.az_offers).length === 20 && "+"}{" "}
                      <i className="fa fa-user" aria-hidden="true"></i><br />
                      <span style={{ fontSize: "80%" }}>{`FBA: ${JSON.parse(product.az_offers).filter(item => item.is_fba).length}`}<br />
                        {`FBM: ${JSON.parse(product.az_offers).filter(item => !item.is_fba).length}`}</span>
                    </td>
                    <td>
                      {product.az_price > 0 ? `+$${productProfit(product, props.filters, prep).toFixed(2)}` : "OOS on AMZ"}
                    </td>
                    <td class="bg" id="roiTt">
                      {product.az_price > 0 ? (productRoi(product, props.filters, prep) * 100).toFixed(2) : "OOS on AMZ"}
                      {product.az_price > 0 && "%"}
                    </td>
                    <td style={{ width: "15vw" }}>
                      <div
                        className="columns is-centered"
                        style={{ display: "flex" }}
                      >
                        <button
                          className={"button is-rounded" + (!localStorage.getItem("clickedActions") ? " hidebtnglow" : "")}
                          id="hideTt"
                          style={{
                            backgroundColor: "#8B3C7E",
                            color: "white",
                            fontWeight: "700",
                            border: "#8B3C7E",
                            fontSize: ".64vw",
                          }}
                          onClick={() => {
                            setHideModalProduct(product);
                          }}
                        >
                          HIDE FROM OTHERS
                        </button>
                        <FavoritesIcon oos={false} url={product.url} favorite={product.favorite} />
                      </div>
                      <div
                        className={"columns is-centered" + (!localStorage.getItem("clickedActions") ? " actions" : "")}
                        style={{ display: "flex", marginTop: "1px" }}
                        onClick={() => localStorage.setItem("clickedActions", true)}
                      >
                        <i class="fa fa-search graphicon tooltip" style={{
                          width: "2.25vw",
                          marginTop: "1px",
                          marginLeft: "3px",
                          height: "2.25vw",
                          borderRadius: "20px",
                          border: "2px solid gray",
                          padding: "0.35vw 0vw",
                          cursor: "pointer",
                          fontSize: "1.35vw",
                        }} aria-hidden="true"
                          onClick={() => setSearchModal(product)}
                          id="searchTt"
                        ><span
                          class="tooltiptext"
                          style={{
                            marginTop: "-25%",
                            fontSize: ".75vw",
                            zIndex: "2000",
                            padding: "3px 6px",
                            border: "1px solid black",
                            fontFamily: "sans-serif",
                          }}
                        >
                            Check in other suppliers
                          </span></i>
                        <i
                          className={`fa ${(loadGated && !props.gatedStatus.hasOwnProperty(product.asin.split("/")[product.asin.split("/").length - 1])) ? "fa-spinner fa-spin" : (props.gatedStatus.hasOwnProperty(product.asin.split("/")[product.asin.split("/").length - 1]) && props.gatedStatus[product.asin.split("/")[product.asin.split("/").length - 1]].restrictions ? (props.gatedStatus[product.asin.split("/")[product.asin.split("/").length - 1]].restrictions.length === 0 ? "fa-unlock" : (props.gatedStatus[product.asin.split("/")[product.asin.split("/").length - 1]].restrictions[0].reasons[0].reasonCode === "APPROVAL_REQUIRED" ? "fa-unlock-alt" : "fa-lock")) : "fa-unlock-alt")} graphicon tooltip`}
                          style={{
                            width: "2.25vw",
                            marginTop: "1px",
                            marginLeft: "3px",
                            height: "2.25vw",
                            borderRadius: "20px",
                            border: "2px solid gray",
                            padding: (loadGated && !props.gatedStatus.hasOwnProperty(product.asin.split("/")[product.asin.split("/").length - 1])) ? "0.15vw" : "0.3vw 0.2vw",
                            cursor: "pointer",
                            fontSize: "1.6vw",
                            color: props.gatedStatus.hasOwnProperty(product.asin.split("/")[product.asin.split("/").length - 1]) && props.gatedStatus[product.asin.split("/")[product.asin.split("/").length - 1]].restrictions ? "white" : "rgb(195, 65, 2)",
                            backgroundColor: props.gatedStatus.hasOwnProperty(product.asin.split("/")[product.asin.split("/").length - 1]) && props.gatedStatus[product.asin.split("/")[product.asin.split("/").length - 1]].restrictions ? (props.gatedStatus[product.asin.split("/")[product.asin.split("/").length - 1]].restrictions.length === 0 ? "green" : (props.gatedStatus[product.asin.split("/")[product.asin.split("/").length - 1]].restrictions[0].reasons[0].reasonCode === "APPROVAL_REQUIRED" ? "orange" : "red")) : "white"
                          }}
                          id="gatedTt"
                          onClick={() => {
                            if (props.sellerId === "none" || props.sellerId === "") {
                              setLoginWAZ(true);
                            }
                            window
                              .open(
                                "https://sellercentral.amazon.com/hz/approvalrequest/restrictions/approve?asin=" +
                                product.asin.split("/")[
                                product.asin.split("/").length - 1
                                ],
                                "_blank"
                              )
                              .focus()
                          }}
                          aria-hidden="true"
                        >
                          <span
                            class="tooltiptext"
                            style={{
                              zIndex: "2000",
                              fontFamily: "sans-serif",
                              marginTop: "-25%",
                              fontSize: ".75vw",
                            }}
                          >
                            Check if Gated
                          </span>
                        </i>
                        <img
                          className="graphicon"
                          alt="keepa"
                          id="keepaTt"
                          onClick={() =>
                            window.open(
                              `https://keepa.com/#!product/1-${product.asin.split("/")[
                              product.asin.split("/").length - 1
                              ]
                              }}`,
                              "_blank"
                            )
                          }
                          src={"/assets/keepa.png"}
                          style={{
                            width: "2.25vw",
                            marginTop: "1px",
                            marginLeft: "3px",
                            height: "2.25vw",
                            borderRadius: "20px",
                            border: "2px solid gray",
                            padding: "3px",
                          }}
                        />
                        <i
                          className="fa fa-exclamation-triangle graphicon tooltip"
                          style={{
                            width: "2.25vw",
                            marginTop: "1px",
                            marginLeft: "3px",
                            height: "2.25vw",
                            borderRadius: "20px",
                            border: "2px solid gray",
                            padding: "0.4vw 0.2vw",
                            cursor: "pointer",
                            fontSize: "1.3vw",
                            color: "darkred",
                          }}
                          id="reportTt"
                          onClick={() => {
                            if (
                              window.confirm(
                                "Report a mismatch?\nNote: Users who find 50 mismatches/out of stock will receive a free subscription!\n\nThe report will be sent to our team for verification. After confirming the mismatch, it will be counted."
                              )
                            ) {
                              reportMismatch(
                                product.asin.split("/")[
                                product.asin.split("/").length - 1
                                ], product.source_name
                              );
                              localStorage.setItem(product.asin.split("/")[product.asin.split("/").length - 1] + "NIU", Date.now() + 30 * 24 * 60 * 60 * 1000);
                            }
                          }}
                          aria-hidden="true"
                        >
                          {" "}
                          <span
                            class="tooltiptext"
                            style={{
                              zIndex: "2000",
                              fontFamily: "sans-serif",
                              marginTop: "-25%",
                              fontSize: ".75vw",
                            }}
                          >
                            Report a mismatch / out of stock product
                          </span>
                        </i>
                      </div>
                    </td>
                  </tr>
                  {props.showAdv && <tr>
                    <td colSpan={"12"} style={{ borderColor: "#E3D0DF" }}>
                      <AdvancedData
                        key={product.asin + "-" + props.page}
                        isItTheProduct={advancedDataProduct === product}
                        advancedDataProduct={advancedDataProduct}
                        setProduct={() => setAdvancedDataProduct(product)}
                        closeAdvancedData={() => setAdvancedDataProduct(null)}
                        isMobile={props.isMobile}
                        setShowAdv={props?.setShowAdv ?? null} />
                    </td></tr>}
                </>
              );
            })}
            {props.isProductsLoading === true && pageRows.length < PAGE_SIZE && <tr><td colSpan={12}><i
              className="fa fa-spinner fa-spin"
              aria-hidden="true"
              style={{ fontSize: "250%", textAlign: 'center', width: "100%" }}
            ></i><br />The scan may take up to 3 minutes.</td></tr>}
            {gatedActive && !hasSeller && <tr><td colSpan={12} style={{ textAlign: "center" }}>
              We can't tell which products you are gated on until your Amazon account is linked, so none are listed here.
              <button style={{ marginLeft: "15px", backgroundColor: "orange", border: "2px solid gray", borderRadius: "30px", cursor: "pointer", boxShadow: "0px 0px 5px 0px black" }} onClick={() => { localStorage.removeItem("no_azlog_popup"); setLoginWAZ(true) }}>Please click here to link your Amazon account.</button>
            </td></tr>}
            {gatedActive && hasSeller && stillFilling && <tr><td colSpan={12} style={{ textAlign: "center" }}>
              <i className="fa fa-spinner fa-spin" aria-hidden="true" style={{ marginRight: "8px" }}></i>
              Checking your eligibility to sell... {gatedProgress.checked} of {gatedProgress.total} ASINs checked.
            </td></tr>}
            {/* Keyed on "no confirmed next page" rather than "page is short": a page can be exactly
                full with its budget spent and row 11 still unknown, and then Next is disabled too --
                this row is the only way forward. */}
            {gatedActive && hasSeller && !stillFilling && (fillExhausted || allAsinsChecked) && !hasNextPage && <tr><td colSpan={12} style={{ textAlign: "center" }}>
              {allAsinsChecked
                ? `Checked all ${gatedProgress.total} ASINs — ${visible.length} of them match this filter, and there are no more to find.`
                : <>
                  Checked {gatedProgress.checked} of {gatedProgress.total} ASINs so far — {visible.length} match this filter. There are more left to check.
                  <br />
                  <button className="button is-success" style={{ marginTop: "8px" }} onClick={keepLooking}>Keep looking</button>
                </>}
            </td></tr>}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="12" style={{ border: "none" }}>
                <button className="button" disabled={props.page === 1} onClick={() => {
                  props.setPage(p => p - 1); window.scrollTo({
                    top: 0,
                    behavior: 'smooth' // for smooth scrolling
                  });
                }} style={{ float: "left", backgroundColor: "#8B3C7E", color: 'white' }}>Previous Page</button>
                <span>Page {props.page}</span>
                {/* Enabled only once a row for the next page is confirmed (the look-ahead fills to
                    page * PAGE_SIZE + 1), so Next never lands on a page that is still empty. */}
                <button className="button" disabled={!hasNextPage} title={!hasNextPage && stillFilling ? "Still checking which products you are ungated on..." : undefined} onClick={() => {
                  props.setPage(p => p + 1); window.scrollTo({
                    top: 0,
                    behavior: 'smooth' // for smooth scrolling
                  });
                }} style={{ float: "right", backgroundColor: "#8B3C7E", color: 'white' }}>Next Page</button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {loginWAZ && localStorage.getItem("no_azlog_popup") !== "1" && (
        <div
          id="modal-js-example"
          className={
            "modal" + (loginWAZ !== null ? " is-active" : "")
          }
        >
          <div className="modal-background"></div>
          <div
            className="modal-content"
            style={{ width: props.isMobile ? "90vw" : "38vw" }}
          >
            <div className="box" style={{ textAlign: "center", fontWeight: "600" }}>
              <p style={{ fontSize: props.isMobile ? "2.5vh" : "1.1vw" }}>
                Log in to your Amazon account to automate the eligibility check!
              </p>
              <br />
              <i
                className="fa fa-unlock graphicon tooltip"
                style={{
                  width: "2.25vw",
                  height: "2.25vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "0.3vw 0.2vw",
                  fontSize: "1.6vw",
                  color: "white",
                  backgroundColor: "green"
                }} />&emsp;GREEN - You CAN sell this item!
              <br /><br />
              <i
                className="fa fa-lock graphicon tooltip"
                style={{
                  width: "2.25vw",
                  height: "2.25vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "0.3vw 0.2vw",
                  fontSize: "1.6vw",
                  color: "white",
                  backgroundColor: "red"
                }} />&emsp;RED - You CANNOT sell this item!
              <br /><br />
              <i
                className="fa fa-unlock-alt graphicon tooltip"
                style={{
                  width: "2.25vw",
                  height: "2.25vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "0.3vw 0.2vw",
                  fontSize: "1.6vw",
                  color: "white",
                  backgroundColor: "orange"
                }} />&emsp;ORANGE - You need to APPLY to sell this item!
              <br /><br /><br />
              <i class="fa fa-arrow-right" aria-hidden="true" style={{ fontSize: "25px", marginRight: "10px" }}></i>
              {user && user.email && <img src="https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_156x32.png" onClick={() => window.open(getAmazonConsentUrl(user.email))} style={{ resizeMode: 'center', cursor: "pointer", boxShadow: "0px 0px 5px 0px gray", borderRadius: "5px", marginBottom: "-8px" }} />}
              <i class="fa fa-arrow-left" aria-hidden="true" style={{ fontSize: "25px", marginLeft: "10px" }}></i>
              <br /><br /><br />
              <div className="columns is-mobile is-centered" style={{ marginBottom: "1%", marginTop: '1%' }}>
                <button
                  className="button is-rounded is-danger is-outlined"
                  style={{ fontSize: props.isMobile ? "1.9vh" : ".95vw" }}
                  onClick={() => {
                    setLoginWAZ(false);
                    localStorage.setItem("no_azlog_popup", "1")
                  }}
                >
                  Do not show this message again
                </button>&emsp;
                <button
                  className="button is-rounded is-danger"
                  style={{ fontSize: props.isMobile ? "1.9vh" : ".95vw" }}
                  onClick={() => {
                    setLoginWAZ(false);
                  }}
                >
                  Close for Now
                </button>
              </div>
            </div>
          </div>
        </div>)}
      {hideModalProduct !== "" && (
        <ModalForHide
          isMobile={props.isMobile}
          setHideModalProduct={setHideModalProduct}
          hideModalProduct={hideModalProduct}
          hideCredits={props.hideCredits}
        />
      )}
      {linkUrl !== "" && (
        <NotDirectLinkPopup
          isMobile={props.isMobile}
          setLinkUrl={setLinkUrl}
          linkUrl={linkUrl}
        />
      )}
      {showMessageObj !== null && (
        <MessageBeforeLinkPopup
          showMessageObj={showMessageObj}
          setShowMessageObj={setShowMessageObj}
        />
      )}
      {modalForVariations &&
        <ModalForVariationsTable
          variations={-999} // Custom flag to fetch variations
          currAsin={modalForVariationsAsin}
          modalForVariations={modalForVariations}
          setModalForVariations={setModalForVariations}
          isMobile={props.isMobile} />
      }
      {searchModal !== null && (
        <div
          id="modal-js-example"
          className={
            "modal" + (searchModal !== null ? " is-active" : "")
          }
        >
          <div className="modal-background" onClick={() => {
            setSearchModal(null);
          }}></div>
          <div
            className="modal-content"
            style={{ width: props.isMobile ? "90vw" : "38vw" }}
          >
            <div className="box" style={{ textAlign: "center" }}>
              <p style={{ fontSize: props.isMobile ? "2.5vh" : "1.1vw" }}>
                Check in other suppliers
              </p>
              <p>
                A specific ASIN can be found from multiple suppliers in Nepeto. By clicking the Nepeto button, you can compare all prices from different suppliers.
              </p>
              <br />
              <img
                className="graphicon"
                alt="ebay"
                onClick={() =>
                  window.open(searchModal.upc !== null ?
                    `https://www.ebay.com/sch/i.html?&_nkw=${searchModal.upc}&_odkw=${searchModal.upc}` :
                    `https://www.ebay.com/sch/i.html?&_nkw=${searchModal.title.split(" | At")[0].slice(0, 40)}&_odkw=${searchModal.title.split(" | At")[0].slice(0, 40)}`,
                    "_blank"
                  )
                }
                src={"/assets/ebay.png"}
                style={{
                  width: "5vw",
                  marginTop: "10px",
                  height: "3vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "0.7vw",
                  objectFit: "contain",
                  cursor: "pointer"
                }}
              />&emsp;&emsp;&emsp;
              <img
                className="graphicon"
                alt="ebay"
                onClick={() =>
                  window.open("https://nepeto.com/retail?asin=" + searchModal.asin.split("/")[searchModal.asin.split("/").length - 1
                  ])}
                src={logo}
                style={{
                  width: "5vw",
                  marginTop: "10px",
                  height: "3vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "3px",
                  objectFit: "contain",
                  cursor: "pointer"
                }}
              />&emsp;&emsp;&emsp;
              <img
                className="graphicon"
                alt="ebay"
                onClick={() =>
                  window.open("https://www.google.com/search?q=" + searchModal.title.split(" | At")[0])}
                src={"https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Google_2015_logo.svg/250px-Google_2015_logo.svg.png"}
                style={{
                  width: "5vw",
                  marginTop: "10px",
                  height: "3vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "0.5vw",
                  objectFit: "contain",
                  cursor: "pointer"
                }}
              />
              <br /><br /><br />
              <div className="columns is-mobile is-centered">
                <button
                  className="button is-rounded is-danger"
                  style={{ fontSize: props.isMobile ? "1.9vh" : ".95vw" }}
                  onClick={() => {
                    setSearchModal(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>)}
      {saveMoney !== null && (
        <div
          id="modal-js-example"
          className={
            "modal" + (saveMoney !== null ? " is-active" : "")
          }
        >
          <div className="modal-background" onClick={() => {
            setSaveMoney(null);
            setSaveMoneyRates({});
          }}></div>
          <div
            className="modal-content"
            style={{ width: props.isMobile ? "90vw" : "38vw" }}
          >
            <div className="box" style={{ textAlign: "center" }}>
              <p style={{ fontSize: props.isMobile ? "2.5vh" : "1.1vw" }}>
                Save Money on <strong>{saveMoney.charAt(0).toUpperCase() + saveMoney.slice(1)}</strong>!
              </p>
              <p>
                Save money using cashback or a gift card from one of these websites!
                <br />
                <span style={{ fontSize: "95%" }}>After you find your desired discount, please apply it in More&gt;Coupon filter to include it in the calculations and find more profitable products.</span>
                <br /><span style={{ fontSize: "85%" }}>*Note that it will search again, so save the ASIN before applying and search for it afterward.</span>
                <br />
              </p>
              <br />
              <img
                className="graphicon"
                alt="gcwiki"
                onClick={() =>
                  window.open("https://www.giftcardwiki.com/gift-cards/" + (saveMoney.charAt(0).toUpperCase() + saveMoney.slice(1)), "_blank")
                }
                src={"https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSOV3EZSZpGuhYiChjIYalZ5ma5QYQlG22RTAqKUuQn5A&s"}
                style={{
                  width: "5vw",
                  marginTop: "10px",
                  height: "3vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "0.7vw",
                  objectFit: "contain",
                  cursor: "pointer"
                }}
              />
              {!saveMoneyRates.hasOwnProperty("cardbear") ?
                <span style={{ marginLeft: "2vw", position: "absolute", marginTop: "3.6vw", fontSize: "0.75vw", textAlign: "center", width: "5vw" }}>Loading...</span> :
                <span style={{ marginLeft: "2vw", position: "absolute", marginTop: "3.6vw", fontSize: "0.7vw", color: saveMoneyRates['cardbear'] === -1 ? "red" : "green", textAlign: "center", width: "5vw" }}>{saveMoneyRates['cardbear'] === -1 ? "" : "Earn up to " + saveMoneyRates['cardbear'].toFixed(1) + "%"}</span>}
              <img
                className="graphicon"
                alt="ebay"
                onClick={() =>
                  window.open("https://www.cardbear.com/", "_blank")
                }
                src={"https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQLVFyxOrlEiIO-lo5ty1xtrxX2mBoNR3rt7Q&s"}
                style={{
                  marginLeft: "2vw",
                  width: "5vw",
                  marginTop: "10px",
                  height: "3vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "0.1vw",
                  objectFit: "contain",
                  cursor: "pointer"
                }}
              />
              {!saveMoneyRates.hasOwnProperty("arbcard") ?
                <span style={{ marginLeft: "2vw", position: "absolute", marginTop: "3.6vw", fontSize: "0.75vw", textAlign: "center", width: "5vw" }}>Loading...</span> :
                <span style={{ marginLeft: "2vw", position: "absolute", marginTop: "3.6vw", fontSize: "0.7vw", color: saveMoneyRates['arbcard'] === -1 ? "red" : "green", textAlign: "center", width: "5vw" }}>{saveMoneyRates['arbcard'] === -1 ? "" : "Earn up to " + saveMoneyRates['arbcard'].toFixed(1) + "%"}</span>}
              <img
                className="graphicon"
                alt="ebay"
                onClick={() =>
                  window.open("https://arbitragecard.com/giftcards/?utm_source=nepeto", "_blank")
                }
                src={"https://arbitragecard.com/wp-content/uploads/2022/07/LOGO.svg"}
                style={{
                  width: "5vw",
                  marginLeft: "2vw",
                  marginTop: "10px",
                  height: "3vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "0.7vw",
                  objectFit: "contain",
                  cursor: "pointer",
                  backgroundColor: "black",
                }}
              />
              <img
                className="graphicon"
                alt="ebay"
                onClick={() =>
                  window.open(`https://www.rakuten.com/r/IG01PR?eeid=28187`, "_blank")
                }
                src={"https://static.rakuten.com/static/svg/rakuten/rak-logo-brand-v1.svg"}
                style={{
                  width: "5vw",
                  marginLeft: "2vw",
                  marginTop: "10px",
                  height: "3vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "3px",
                  objectFit: "contain",
                  cursor: "pointer"
                }}
              />
              <img
                className="graphicon"
                alt="ebay"
                onClick={() =>
                  window.open(`https://www.befrugal.com/rs/KNCETGG/`, "_blank")
                }
                src={"https://icdn.bfrl.us/headerfooterimages/logo.png"}
                style={{
                  width: "5vw",
                  marginLeft: "2vw",
                  marginTop: "10px",
                  height: "3vw",
                  borderRadius: "20px",
                  border: "2px solid gray",
                  padding: "0.5vw",
                  objectFit: "contain",
                  cursor: "pointer"
                }}
              />
              <br /><br /><br /><br />
              <div className="columns is-mobile is-centered">
                <button
                  className="button is-rounded is-danger"
                  style={{ fontSize: props.isMobile ? "1.9vh" : ".95vw" }}
                  onClick={() => {
                    setSaveMoney(null);
                    setSaveMoneyRates({});
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>)}
    </>
  );
}
